/**
 * OptimizationCard
 *
 * Displays the optimization window for a substep (e.g., male sperm optimization).
 * Shows biological cycle, lifestyle factors, supplements, and age-based timing guidance.
 */

import React, { useState } from "react";
import { View, Text, Pressable } from "react-native";
import {
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Zap,
  Leaf,
  Pill,
  Clock,
} from "lucide-react-native";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { Collapsible } from "@louise/moon-ui";
import { SupplementRow } from "@src/packages/content";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useTranslations } from "@src/packages/i18n";
import type {
  OptimizationWindow,
  OptimizationItem,
  EvidenceLevel,
  PlanAction,
} from "../plan.types";

/** Splits "CoQ10 (200-600 mg/day)" → { name: "CoQ10", dosage: "200-600 mg/day" }. */
function splitNameAndDosage(title: string): {
  name: string;
  dosage?: string;
} {
  const match = title.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (match) {
    return { name: match[1]!.trim(), dosage: match[2]!.trim() };
  }
  return { name: title };
}

type Props = {
  optimization: OptimizationWindow;
  userAge?: number;
  onAction?: (action: PlanAction) => void;
};

function EvidenceBadge({ level }: { level: EvidenceLevel }) {
  const colors = {
    strong: { bg: "rgba(78, 115, 66, 0.1)", text: EMBER.colors.green },
    moderate: { bg: "rgba(176, 128, 48, 0.1)", text: EMBER.colors.gold },
    emerging: { bg: "rgba(90, 77, 69, 0.1)", text: EMBER.colors.textMid },
  };
  const c = colors[level];
  return (
    <View
      style={{
        backgroundColor: c.bg,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 9,
          color: c.text,
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {level}
      </Text>
    </View>
  );
}

function OptItem({
  item,
  onAction,
}: {
  item: OptimizationItem;
  onAction?: (action: PlanAction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const capturePress = usePressCapture("OptItem");

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(item.title));
        setExpanded(!expanded);
      }}
      style={{ gap: 4 }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Zap
          size={12}
          color={
            item.impact === "high"
              ? EMBER.colors.warm
              : item.impact === "moderate"
                ? EMBER.colors.gold
                : EMBER.colors.textLight
          }
        />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.text,
            fontWeight: "500",
            flex: 1,
          }}
        >
          {item.title}
        </Text>
        <EvidenceBadge level={item.evidence} />
      </View>

      {expanded ? (
        <View style={{ marginLeft: 20, marginTop: 4, gap: 6 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "400",
            }}
          >
            {item.education}
          </Text>
          {item.action ? (
            <Pressable
              onPress={() => {
                capturePress(pressLabel(item.action!.label));
                onAction?.(item.action!);
              }}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 6,
                backgroundColor: "rgba(184, 74, 22, 0.08)",
                alignSelf: "flex-start",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.warm,
                  fontWeight: "600",
                }}
              >
                {item.action.label}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </Pressable>
  );
}

export default function OptimizationCard({
  optimization,
  userAge,
  onAction,
}: Props) {
  const t = useTranslations();
  const captureCardPress = usePressCapture("OptimizationCard");
  const [showLifestyle, setShowLifestyle] = useState(false);
  const [showSupplements, setShowSupplements] = useState(false);

  // Find age-appropriate guidance
  const ageGuidance = userAge
    ? optimization.ageGuidance.find(
        (g) => userAge >= g.range[0] && userAge <= g.range[1],
      )
    : undefined;

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        marginTop: 16,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <View style={{ padding: 16, gap: 8 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <TrendingUp size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              color: EMBER.colors.text,
              fontWeight: "600",
            }}
          >
            {t("homepage.optimization_card.optimization_window")}
          </Text>
        </View>

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 19,
            color: EMBER.colors.textMid,
            fontWeight: "400",
          }}
        >
          {t("homepage.optimization_card.window_description", {
            params: {
              cycle: optimization.biologicalCycle,
              window: optimization.optimizationPeriod,
              retest: optimization.retestAction.toLowerCase(),
            },
          })}
        </Text>

        {/* Age-specific timing guidance — inline, no chrome stripe */}
        {ageGuidance ? (
          <View style={{ marginTop: 4 }}>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <Clock size={13} color={EMBER.colors.warm} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.warm,
                  fontWeight: "600",
                }}
              >
                {ageGuidance.bracket}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 19,
                color: EMBER.colors.text,
                fontWeight: "400",
                marginTop: 4,
              }}
            >
              {ageGuidance.explanation}
            </Text>
          </View>
        ) : null}
      </View>

      {/* Lifestyle section */}
      <View
        style={{ borderTopWidth: 1, borderTopColor: "rgba(94, 110, 62, 0.1)" }}
      >
        <Pressable
          onPress={() => {
            captureCardPress(
              pressLabel(t("homepage.shared.lifestyle_factors")),
            );
            setShowLifestyle(!showLifestyle);
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            padding: 14,
            gap: 8,
          }}
        >
          <Leaf size={16} color={EMBER.colors.teal} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.text,
              fontWeight: "500",
              flex: 1,
            }}
          >
            {t("homepage.shared.lifestyle_factors")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textFaint,
              fontWeight: "400",
            }}
          >
            {t("homepage.optimization_card.count_items", {
              params: { count: String(optimization.lifestyle.length) },
            })}
          </Text>
          {showLifestyle ? (
            <ChevronUp size={16} color={EMBER.colors.textLight} />
          ) : (
            <ChevronDown size={16} color={EMBER.colors.textLight} />
          )}
        </Pressable>

        <Collapsible expanded={showLifestyle}>
          <View style={{ paddingHorizontal: 14, paddingBottom: 14, gap: 10 }}>
            {optimization.lifestyle.map((item) => (
              <OptItem key={item.title} item={item} onAction={onAction} />
            ))}
          </View>
        </Collapsible>
      </View>

      {/* Supplements section */}
      <View
        style={{ borderTopWidth: 1, borderTopColor: "rgba(94, 110, 62, 0.1)" }}
      >
        <Pressable
          onPress={() => {
            captureCardPress("supplements_section");
            setShowSupplements(!showSupplements);
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            padding: 14,
            gap: 8,
          }}
        >
          <Pill size={16} color={EMBER.colors.teal} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.text,
              fontWeight: "500",
              flex: 1,
            }}
          >
            {t("homepage.optimization_card.supplements")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textFaint,
              fontWeight: "400",
            }}
          >
            {t("homepage.optimization_card.count_items", {
              params: { count: String(optimization.supplements.length) },
            })}
          </Text>
          {showSupplements ? (
            <ChevronUp size={16} color={EMBER.colors.textLight} />
          ) : (
            <ChevronDown size={16} color={EMBER.colors.textLight} />
          )}
        </Pressable>

        <Collapsible expanded={showSupplements}>
          <View style={{ paddingHorizontal: 14, paddingBottom: 14, gap: 10 }}>
            {optimization.supplements.map((item) => {
              const { name, dosage } = splitNameAndDosage(item.title);
              return (
                <SupplementRow
                  key={item.title}
                  name={name}
                  dosage={dosage}
                  description={item.education}
                  evidence={item.evidence}
                />
              );
            })}
          </View>
        </Collapsible>
      </View>
    </View>
  );
}
